<script setup>
import addItem from '../../components/navigation/settings/addItem.vue'
import editItem from '../../components/navigation/settings/editItem.vue'
import NavigationItemRow from '../../components/navigation/settings/NavigationItemRow.vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import draggable from 'vuedraggable'
import SlideoutWithSlots from "../../components/elements/slideoutWithSlots.vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'

const notificationStore = useNotificationStore()

const props = defineProps({
    title: {
        type: String,
        required: false,
        default: () => _mft('setting:navigation.title')
    },
    navigationType: {
        type: String,
        required: true
    }
})

const navigationItems = ref([])
const deletedIds = ref([])
const snapshot = ref('[]')
const adding = ref(null)
const editingItem = ref(null)
const previewDevice = ref('desktop')
const savingState = ref('')
const contentLoaded = ref(false)
const dragging = ref(false)

let tmpKeyCounter = 0

const withKeys = (item) => {
    item._key = item.id ? `id-${item.id}` : `tmp-${tmpKeyCounter++}`
    item.children = (item.children ?? []).map(withKeys)
    return item
}

const serialize = (items) => JSON.stringify(items.map(item => ({
    key: item._key,
    label: item.label,
    visibility: item.visibility ?? null,
    children: item.children.map(child => ({
        key: child._key,
        label: child.label,
        visibility: child.visibility ?? null,
    })),
})))

const isDirty = computed(() => {
    return deletedIds.value.length > 0 || serialize(navigationItems.value) !== snapshot.value
})

const loadItems = async () => {
    const res = await axios.get(route('settings.navigation.show', {type: props.navigationType}))
    navigationItems.value = res.data.items.map(withKeys)
    snapshot.value = serialize(navigationItems.value)
    deletedIds.value = []
    contentLoaded.value = true
}

const discardChanges = () => {
    loadItems()
}

const startAdding = (parent = null, type = null) => {
    editingItem.value = null
    adding.value = { parent, type }
}

const appendItem = (item) => {
    const newItem = withKeys({ ...item })
    if (adding.value?.parent) {
        adding.value.parent.children.push(newItem)
    } else {
        navigationItems.value.push(newItem)
    }
    adding.value = null
}

const removeItem = (item, parent = null) => {
    [...item.children, item].forEach(entry => {
        if (entry.id) {
            deletedIds.value.push(entry.id)
        }
    })

    const list = parent ? parent.children : navigationItems.value
    const index = list.indexOf(item)
    if (index > -1) {
        list.splice(index, 1)
    }
}

const selectForEditing = (item) => {
    adding.value = null
    editingItem.value = item
}

const slideoutClosed = () => {
    adding.value = null
    editingItem.value = null
}

// Only one level of nesting is supported, so items that have their own
// children can't be dropped into another item's dropdown.
const canMove = (evt) => {
    if (evt.dragged.contains(evt.to)) {
        return false
    }
    return !(evt.to.dataset.level === 'child' && evt.draggedContext.element.children.length > 0)
}

const withOrder = (items) => items.map((item, i) => ({
    ...item,
    order: i,
    children: item.children.map((child, ci) => ({ ...child, order: ci })),
}))

const storeNavigation = async () => {
    savingState.value = 'waiting'
    try {
        const res = await axios.post(route('settings.navigation.store'), {
            items: withOrder(navigationItems.value),
            type: props.navigationType
        })

        if (!res.data.success) {
            throw new Error('Navigation could not be saved')
        }

        for (const id of deletedIds.value) {
            try {
                await axios.delete(route('settings.navigation.destroy', id))
            } catch (e) {
                if (e.response?.status !== 404) {
                    throw e
                }
            }
        }

        savingState.value = 'success'
    } catch (e) {
        savingState.value = 'error'
        notificationStore.addNotification({
            type: 'error',
            title: _mft('setting:navigation.save.error'),
            dismissAfter: 5000,
        })
    }

    await loadItems()
}

const warnOnUnsavedChanges = (event) => {
    if (isDirty.value) {
        event.preventDefault()
        event.returnValue = ''
    }
}

onMounted(() => {
    loadItems()
    window.addEventListener('beforeunload', warnOnUnsavedChanges)
})

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', warnOnUnsavedChanges)
})
</script>

<template>
    <div class="pb-2">
        <h3 v-if="props.title" class="mb-4">{{ props.title }}</h3>

        <div
            v-if="contentLoaded && navigationItems.length > 0"
            class="mb-4"
        >
            <div class="mb-2 flex items-end justify-between gap-4">
                <div>
                    <span class="block text-xs font-medium text-gray-500">{{ _mft('shared:preview') }}</span>
                    <span class="block text-xs text-gray-400">{{ _mft('setting:navigation.preview.themeDisclaimer') }}</span>
                </div>
                <div class="inline-flex rounded-md border border-gray-200 p-0.5">
                    <button
                        type="button"
                        class="rounded px-2 py-1 text-xs"
                        :class="previewDevice === 'desktop' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900'"
                        :aria-pressed="previewDevice === 'desktop'"
                        @click="previewDevice = 'desktop'"
                    >
                        <i class="fa-regular fa-desktop mr-1"></i>{{ _mft('setting:navigation.preview.computer') }}
                    </button>
                    <button
                        type="button"
                        class="rounded px-2 py-1 text-xs"
                        :class="previewDevice === 'mobile' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900'"
                        :aria-pressed="previewDevice === 'mobile'"
                        @click="previewDevice = 'mobile'"
                    >
                        <i class="fa-regular fa-mobile-screen mr-1"></i>{{ _mft('setting:navigation.preview.mobile') }}
                    </button>
                </div>
            </div>

            <div class="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <nav
                    v-if="previewDevice === 'desktop'"
                    class="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md bg-white px-4 py-3 shadow-sm"
                >
                    <span class="mr-2 h-6 w-6 shrink-0 rounded bg-gray-200" aria-hidden="true"></span>
                    <div
                        v-for="item in navigationItems"
                        :key="item._key"
                        class="group/preview relative"
                    >
                        <span class="flex items-center gap-1 whitespace-nowrap text-xs font-bold text-gray-800">
                            {{ item.label }}
                            <i
                                v-if="item.children.length > 0"
                                class="fa-solid fa-chevron-down text-[0.6rem] text-gray-400"
                            ></i>
                        </span>
                        <div
                            v-if="item.children.length > 0"
                            class="invisible absolute left-0 top-full z-10 mt-2 min-w-40 rounded-md border border-gray-100 bg-white py-1 opacity-0 shadow-md transition-opacity group-hover/preview:visible group-hover/preview:opacity-100"
                        >
                            <div
                                v-for="child in item.children"
                                :key="child._key"
                                class="whitespace-nowrap px-3 py-1.5 text-xs text-gray-700"
                            >{{ child.label }}</div>
                        </div>
                    </div>
                </nav>

                <div
                    v-else
                    class="mx-auto w-full max-w-xs overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                >
                    <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                        <span class="h-6 w-6 rounded bg-gray-200" aria-hidden="true"></span>
                        <i class="fa-solid fa-xmark text-gray-400"></i>
                    </div>
                    <ul class="py-2">
                        <li
                            v-for="item in navigationItems"
                            :key="item._key"
                        >
                            <div class="px-4 py-2 text-xs font-bold text-gray-800">{{ item.label }}</div>
                            <div
                                v-for="child in item.children"
                                :key="child._key"
                                class="py-1.5 pl-8 pr-4 text-xs text-gray-600"
                            >{{ child.label }}</div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div
            v-if="contentLoaded && navigationItems.length === 0"
            class="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center"
        >
            <i class="fa-regular fa-bars mb-3 text-2xl text-gray-300"></i>
            <p class="mb-1 font-medium text-gray-900">{{ _mft('setting:navigation.empty.title') }}</p>
            <p class="mx-auto mb-4 max-w-sm text-sm text-gray-500">{{ _mft('setting:navigation.empty.description') }}</p>
        </div>

        <draggable
            v-show="navigationItems.length > 0"
            v-model="navigationItems"
            item-key="_key"
            tag="ul"
            handle=".nav-drag-handle"
            group="navigation-items"
            ghost-class="nav-item-ghost"
            :move="canMove"
            class="divide-y divide-gray-100 overflow-visible rounded-lg border border-gray-200 bg-white"
            @start="dragging = true"
            @end="dragging = false"
        >
            <template #item="{ element: item }">
                <li class="first:rounded-t-lg last:rounded-b-lg">
                    <NavigationItemRow
                        :item="item"
                        @edit="selectForEditing(item)"
                        @remove="removeItem(item)"
                        @add-child="startAdding(item)"
                    />

                    <div
                        v-if="item.children.length > 0 || dragging"
                        class="relative ml-7 border-l border-gray-200 pl-2"
                        :class="item.children.length > 0 ? 'mb-2' : ''"
                    >
                        <draggable
                            v-model="item.children"
                            item-key="_key"
                            tag="ul"
                            handle=".nav-drag-handle"
                            group="navigation-items"
                            ghost-class="nav-item-ghost"
                            data-level="child"
                            :move="canMove"
                            :class="dragging && item.children.length === 0 ? 'mb-2 mr-3 min-h-9 rounded-md border border-dashed border-gray-200' : ''"
                            @start="dragging = true"
                            @end="dragging = false"
                        >
                            <template #item="{ element: child }">
                                <li class="rounded-md">
                                    <NavigationItemRow
                                        :item="child"
                                        is-child
                                        @edit="selectForEditing(child)"
                                        @remove="removeItem(child, item)"
                                    />
                                </li>
                            </template>
                        </draggable>

                        <button
                            v-if="item.children.length > 0"
                            type="button"
                            class="ml-2 mt-1 inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-2.5 py-1 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-900"
                            @click="startAdding(item)"
                        >
                            <i class="fa-regular fa-plus"></i>
                            {{ _mft('setting:navigation.item.addTo', {label: item.label}) }}
                        </button>
                    </div>
                </li>
            </template>
        </draggable>

        <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
            <button-more-options
                style-type="secondary"
                size="md"
                :label="_mft('setting:navigation.item.add')"
                options-class="min-w-48"
            >
                <template #icon>
                    <i class="fa-regular fa-plus"></i>
                </template>
                <button-more-options-option
                    class="whitespace-nowrap"
                    @click="startAdding(null, 'page')"
                >
                    {{ _mft('navigation:setting.addPage') }}
                </button-more-options-option>
                <button-more-options-option
                    class="whitespace-nowrap"
                    @click="startAdding(null, 'landingpage')"
                >
                    {{ _mft('navigation:setting.addLandingPage') }}
                </button-more-options-option>
                <button-more-options-option
                    class="whitespace-nowrap"
                    @click="startAdding(null, 'course')"
                >
                    {{ _mft('navigation:setting.addCourse') }}
                </button-more-options-option>
            </button-more-options>

            <div class="flex items-center gap-2">
                <span
                    v-if="isDirty"
                    class="mr-1 inline-flex items-center gap-1.5 text-xs text-amber-700"
                >
                    <span class="h-1.5 w-1.5 rounded-full bg-amber-500"></span>
                    {{ _mft('setting:navigation.unsavedChanges') }}
                </span>
                <button-secondary
                    v-if="isDirty"
                    @click="discardChanges"
                >{{ _mft('setting:navigation.discard') }}</button-secondary>
                <button-success
                    :disabled="savingState === 'waiting' || !isDirty"
                    :state="savingState"
                    @click="storeNavigation"
                >{{ _mft('shared:action.save') }}</button-success>
            </div>
        </div>

        <slideout-with-slots
            v-if="adding !== null || editingItem !== null"
            @close="slideoutClosed"
        >
            <template #body>
                <addItem
                    v-if="adding !== null"
                    :navigation-type="props.navigationType"
                    :initial-type="adding.type"
                    @itemAdded="appendItem"
                    @cancel="adding = null"
                />
                <editItem
                    v-if="editingItem !== null"
                    :navigation-type="props.navigationType"
                    :item="editingItem"
                    @cancel="editingItem = null"
                    @itemUpdated="editingItem = null"
                />
            </template>
        </slideout-with-slots>
    </div>
</template>

<style scoped>
:deep(.nav-item-ghost) {
    @apply rounded-md bg-gray-100 opacity-60;
}
</style>
